'use client';

// Écran de connexion du back-office — délibérément distinct de /auth
// (grand public) : pas d'enseigne TonMonni consommateur, pas de choix
// téléphone/e-mail/Google, un seul champ identifiant + mot de passe. Sert
// aussi de porte de sortie pour une gérante ou une membre qui atterrirait
// ici (lien retrouvé, favori, hôte admin visité par erreur) : son compte
// existe mais n'a pas les droits, elle voit un refus explicite, jamais un
// formulaire qui laisserait croire que cette porte lui est ouverte.
import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';

type Screen = 'loading' | 'form' | 'forbidden';

function readError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const message = err.body['message'];
    if (typeof message === 'string' && message) return message;
  }
  return fallback;
}

export function AdminLoginScreen() {
  const router = useRouter();
  const { logout } = useAuth();
  const [screen, setScreen] = useState<Screen>('loading');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // /api/admin/me sert de sonde : 200 = déjà une session admin (on saute la
  // connexion), 403 = une session existe mais sans droits (on le dit tout de
  // suite), tout le reste (401 en pratique) = pas de session, formulaire.
  const probe = useCallback(async () => {
    try {
      await api('/api/admin/me');
      router.replace('/admin');
    } catch (err) {
      setScreen(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'form');
    }
  }, [router]);

  useEffect(() => {
    void probe();
  }, [probe]);

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      await api('/api/auth/login', {
        method: 'POST',
        body: { identifier: identifier.trim(), password },
      });
    } catch (err) {
      setError(readError(err, 'Identifiants incorrects.'));
      setPending(false);
      return;
    }
    try {
      await api('/api/admin/me');
      router.replace('/admin');
    } catch {
      // Identifiants valides mais compte sans droits admin — ne pas laisser
      // une session ouverte sur cet hôte, la refermer avant d'afficher le
      // refus.
      await logout();
      setScreen('forbidden');
    } finally {
      setPending(false);
    }
  };

  if (screen === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <p className="m-0 text-[14px] text-white/60">Chargement…</p>
      </div>
    );
  }

  if (screen === 'forbidden') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-navy px-6 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white">
          <Icon name="shield" size={24} />
        </span>
        <p className="m-0 font-display text-[20px] font-bold text-white">Accès réservé</p>
        <p className="m-0 max-w-[360px] text-[14px] text-white/70">
          Cet espace est celui de l’équipe TonMonni. Ce compte n’a pas les droits nécessaires pour y
          entrer.
        </p>
        <a href="/" className="text-[14px] font-semibold text-white underline">
          Retour au site
        </a>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-6">
      <div className="w-full max-w-[380px] rounded-card bg-white p-[28px]">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white">
            <Icon name="shield" size={22} />
          </span>
          <p className="m-0 font-display text-[19px] font-bold text-navy">Espace administrateur</p>
          <p className="m-0 text-[13px] text-mut">Réservé à l’équipe TonMonni.</p>
        </div>

        <form
          className="flex flex-col gap-3.5"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field
            label="Identifiant"
            icon="users"
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
          <Field
            label="Mot de passe"
            icon="lock"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error ? <p className="m-0 text-[13px] font-medium text-red">{error}</p> : null}
          <Button type="submit" disabled={pending || !identifier.trim() || !password}>
            {pending ? 'Connexion…' : 'Se connecter'}
          </Button>
        </form>
      </div>
    </div>
  );
}
